import { ft } from '../core/units.js';
import { EVENT } from './events.js';

/**
 * How out of it you are, and what that buys and costs.
 *
 * One bar, on its own clock, running on real seconds rather than the game's
 * minutes — because being high is a thing that happens to a body and not a
 * thing that happens to a shift. Skipping the clock forward does not sober you
 * up and does not get you high.
 *
 * The bar never makes anybody suspicious of you by itself. That is the rule
 * that keeps it interesting: impairment is not a crime, it is an amplifier.
 * Everything you do while it is up is believed harder, so a shift where nothing
 * goes wrong costs nothing at all and a shift where one thing does costs double.
 *
 * What it buys:
 *
 *   TOLERANCE  you can set a table from further away and less squarely, which
 *              is the single biggest lever on how long a room takes
 *   GHOSTING   the anchors render. The only time in the game they ever do, and
 *              only once you have actually read the sheet — the drug does not
 *              tell you where the tables go, it lets you see what you read
 *   ACUITY     you hear further
 *   CHATTER    the noise the building makes resolves into words
 *
 * What it costs:
 *
 *   BELIEF     every witness is more sure of whatever they think they saw
 *   TELLS      anybody close and facing you can see it, which is a state they
 *              can form an opinion about like any other
 *   SCENT      one of them clings to your uniform after the effect has gone
 *   REACTION   you turn and accelerate more slowly — but only while somebody
 *              is already after you
 *
 * Every one of those is on the perception side. None of them is on the clock,
 * and that is the design rather than an accident of tuning.
 *
 * The first pass had it the other way round: a quarter off your top speed, so a
 * whole shift high came out thirty-five minutes behind a sober one. That is a
 * coherent rule and it is the wrong one, because it makes the question "can I
 * afford the minutes" — which the player answers once, at the start of the
 * shift, with a calculator. Moving the whole cost onto perception makes the
 * question "can I afford to be noticed", which is a different question every
 * time somebody walks round a corner.
 *
 * So being out of it is genuinely good at the job and genuinely dangerous, and
 * the trade is risk against time rather than time against time. The reaction
 * penalty survives because being slow to react is a real part of it — but it
 * only applies once somebody is actually after you, which is the only moment
 * reacting is a thing you are doing. Walking a cart down an empty corridor is
 * not a chase.
 */

/** Multiplier on a witness's confidence at full impairment. */
const BELIEF = 0.7;

/**
 * How much slower you turn and accelerate at full impairment — in a chase.
 *
 * Bigger than it was, because it now applies almost never. Sober you turn on a
 * sixpence; a joint and somebody on your heels and you do not, and that is the
 * moment it is supposed to matter.
 */
const REACTION = 0.55;

/**
 * Extra placement reach at full impairment.
 *
 * This is the whole of what being out of it buys, and it has to be worth
 * having. A round set from six and a half feet away instead of five and a half
 * is a step and a half you do not take, thirty-odd times a room.
 */
const TOLERANCE = 1.15;

/** The level at which your hands are as steady as they are going to get. */
const STEADY_BY = 0.55;

/** Below this nothing has happened to you yet. */
const NOTICEABLE = 0.15;

/** Feet. What you hear sober, and the most it grows by. */
const EARSHOT = 20;
const ACUITY = 60;

export function createImpairment({ catalogue, bus, now }) {
  /** Every dose currently working through you. */
  let doses = [];
  /** 0..1, decaying on the game clock rather than the body's. */
  let scent = 0;
  let scentUntil = 0;
  let scentFrom = null;
  /** How long the smell currently on you was going to last, for the fade. */
  let scentOver = 1;

  const minutes = () => (typeof now === 'function' ? now() : 0);

  /** A dose's contribution right now: up the onset, flat, then down the fade. */
  function strength(dose) {
    const kind = catalogue[dose.kind];
    if (!kind) return 0;
    const t = dose.age;
    if (t < kind.onset) return t / kind.onset;
    if (t < kind.onset + kind.hold) return 1;
    const falling = t - kind.onset - kind.hold;
    if (falling >= kind.fade) return 0;
    return 1 - falling / kind.fade;
  }

  function sum(field) {
    let total = 0;
    for (const dose of doses) total += strength(dose) * (catalogue[dose.kind]?.[field] ?? 0);
    return Math.max(0, Math.min(1, total));
  }

  return {
    /**
     * Takes something. Emits it, because putting something in your mouth in a
     * corridor is a thing that happens in the world and somebody may be
     * standing there.
     */
    consume(kind, position) {
      const definition = catalogue[kind];
      if (!definition) return false;
      doses = [...doses, { kind, age: 0 }];
      if (definition.scent) {
        scent = Math.max(scent, definition.scent.strength);
        scentUntil = minutes() + definition.scent.minutes;
        scentOver = definition.scent.minutes;
        scentFrom = definition.name;
      }
      bus?.emit(EVENT.CONSUME, {
        actor: 'player',
        kind,
        position: position ? { ...position } : undefined,
      });
      return true;
    },

    /** Real seconds. The body's own clock. */
    update(dt) {
      if (doses.length) {
        for (const dose of doses) dose.age += dt;
        doses = doses.filter((dose) => strength(dose) > 0 || dose.age < 0.5);
      }
      if (scent > 0) {
        const left = scentUntil - minutes();
        scent = left <= 0 ? 0 : Math.min(scent, left / scentOver);
        if (scent <= 0) scentFrom = null;
      }
    },

    get level() {
      return sum('impairment');
    },
    get alert() {
      return sum('alert');
    },
    get scent() {
      return scent;
    },
    get scentFrom() {
      return scentFrom;
    },
    /** Anything at all in you, including the ones that do nothing to you. */
    get active() {
      return doses.length > 0;
    },

    // --- what it buys and costs ---------------------------------------------

    /** Multiplier a witness's confidence is put through. Never below one. */
    get belief() {
      return 1 + this.level * BELIEF;
    },
    /**
     * Multiplier on turn rate and acceleration, for when somebody is after you.
     *
     * The caller decides whether that is true, because the caller is the only
     * thing that knows. Read it unconditionally and it is a tax on every leg of
     * every trip, which is the version this used to be.
     */
    get reaction() {
      return 1 - this.level * REACTION + this.alert * 0.1;
    },
    /**
     * Multiplier on how fast you can cross the building. Always one.
     *
     * Kept as a named, constant getter rather than deleted, so that the
     * decision is visible at the place somebody would go looking to reinstate
     * it. Alertness still buys a little, because coffee making you brisk is
     * not the rule being avoided here.
     */
    get pace() {
      return 1 + this.alert * 0.05;
    },
    /** Multiplier on how close and how square you have to be to set a table. */
    get tolerance() {
      return 1 + this.level * TOLERANCE;
    },
    /**
     * How steady your hands are on something that does not want to be stood on.
     *
     * The one benefit that is not about the table. A cart is two castors and a
     * shelf; sober you cannot hold it straight at speed, and out of it you can,
     * because you are not fighting it. It is a state rather than an upgrade —
     * it is on while the bar is up and gone when it is not, and it buys you
     * nothing at all if anybody is watching you do it.
     */
    get steadiness() {
      // Full well before the bar is. You do not have to be very far gone to
      // stop fighting a cart; you have to be far enough gone not to tense up.
      // Reading it straight off the level meant a pen wearing thin halfway
      // down a corridor put the load into a wall.
      return Math.min(1, this.level / STEADY_BY);
    },

    /** Metres. How far away a noise still reaches you. */
    get earshot() {
      return ft(EARSHOT + this.level * ACUITY);
    },
    /**
     * Metres. The same, for a body that has had nothing.
     *
     * Read-only, and it exists so that "how much sharper than sober is this"
     * is one division rather than a constant copied into a second file. The
     * audio layer stretches how far you can hear by exactly this ratio, which
     * keeps acuity a single number with one definition.
     */
    get baseEarshot() {
      return ft(EARSHOT);
    },
    get ghosting() {
      return this.level >= NOTICEABLE;
    },
    get chatter() {
      return this.level >= NOTICEABLE * 2;
    },

    /**
     * What somebody standing close and facing you would notice.
     *
     * Returned as conduct, which is the same shape as standing somewhere you
     * should not be — a state rather than an event, noticed by whoever can see
     * it, believed more the longer they look. Nothing here decides it matters.
     */
    tells() {
      const out = [];
      const level = this.level;
      if (level >= NOTICEABLE) {
        for (const dose of doses) {
          const kind = catalogue[dose.kind];
          if (kind?.tell && strength(dose) > 0.3) {
            out.push({ kind: 'impaired', detail: kind.tell });
            break;
          }
        }
      }
      // Faint still counts. A smell that has to be strong to be noticed is a
      // smell that stops mattering exactly when the effect does, which is the
      // one thing it is there not to do.
      if (scent > 0.05) out.push({ kind: 'scent', detail: `smells of ${scentFrom?.toLowerCase()}` });
      return out;
    },

    /** The locker room. A clean uniform is the only thing that takes it off. */
    changeUniform() {
      const had = scent > 0;
      scent = 0;
      scentUntil = 0;
      scentFrom = null;
      return had;
    },

    toJSON: () => ({ doses: doses.map((d) => ({ ...d })), scent, scentUntil, scentFrom }),
    load(state) {
      doses = (state?.doses ?? []).map((d) => ({ ...d }));
      scent = state?.scent ?? 0;
      scentUntil = state?.scentUntil ?? 0;
      scentFrom = state?.scentFrom ?? null;
    },
    clear() {
      doses = [];
      scent = 0;
      scentUntil = 0;
      scentFrom = null;
    },
  };
}
